import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import {
	ICreateQuestionnaireTemplateThunkArgs,
	IGetQuestionnaireTemplateThunkArgs,
	IQuestionnaireTemplateResponse,
	IQuestionnaireTemplate,
	IGetQuestionnaireTemplatesThunkArgs,
	IDeleteQuestionnaireTemplateThunkArgs,
} from 'src/features/questionnaire_template/domain/types';
import { questionnnaireTemplateApi } from 'src/features/questionnaire_template/infrastructure/api';
import { handleThunkError } from 'src/utils/handleThunkError';

export const QUESTIONNAIRE_TEMPLATE_LOADING_KEY = 'questionnaire_template';

export const createQuestionnaireTemplate = createAsyncThunk<
	IQuestionnaireTemplateResponse,
	ICreateQuestionnaireTemplateThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'questionnaire_template/createQuestionnaireTemplate',
	async (
		{ requestBody, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: QUESTIONNAIRE_TEMPLATE_LOADING_KEY,
					loading: true,
				}),
			);
			const response =
				await questionnnaireTemplateApi.createQuestionnaireTemplate(
					requestBody,
					siteSlug,
				);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to create questionnaire template',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: QUESTIONNAIRE_TEMPLATE_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const getQuestionnaireTemplate = createAsyncThunk<
	IQuestionnaireTemplateResponse,
	IGetQuestionnaireTemplateThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'questionnaire_template/getQuestionnaireTemplate',
	async (
		{ questionnaireTemplateId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: QUESTIONNAIRE_TEMPLATE_LOADING_KEY,
					loading: true,
				}),
			);
			const response =
				await questionnnaireTemplateApi.getQuestionnaireTemplate(
					questionnaireTemplateId,
					siteSlug,
				);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch questionnaire template',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: QUESTIONNAIRE_TEMPLATE_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const getQuestionnaireTemplates = createAsyncThunk<
	IQuestionnaireTemplate[],
	IGetQuestionnaireTemplatesThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'questionnaire_template/getQuestionnaireTemplates',
	async (
		{ requestParams, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: QUESTIONNAIRE_TEMPLATE_LOADING_KEY,
					loading: true,
				}),
			);
			const response =
				await questionnnaireTemplateApi.getQuestionnaireTemplates(
					requestParams,
					siteSlug,
				);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch questionnaire templates',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: QUESTIONNAIRE_TEMPLATE_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const deleteQuestionnaireTemplate = createAsyncThunk<
	void,
	IDeleteQuestionnaireTemplateThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'questionnaire_template/deleteQuestionnaireTemplate',
	async (
		{ questionnaireTemplateId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: QUESTIONNAIRE_TEMPLATE_LOADING_KEY,
					loading: true,
				}),
			);
			const response =
				await questionnnaireTemplateApi.deleteQuestionnaireTemplate(
					questionnaireTemplateId,
					siteSlug,
				);

			onSuccess?.();
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to delete questionnaire template',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: QUESTIONNAIRE_TEMPLATE_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);
